import React from 'react';
import { Camera, MapPin, Film, Calendar, TrendingUp, Award, Aperture } from 'lucide-react';
interface InstagramPostVerticalProps {
  title: string;
  type: 'roll' | 'lab' | 'tip' | 'community' | 'feature' | 'quote' | 'stats' | 'gear';
  image?: string;
  imagePosition?: 'top' | 'bottom' | 'full' | 'split';
  content: React.ReactNode;
  subtitle?: string;
}
export function InstagramPostVertical({
  title,
  type,
  content,
  subtitle,
  image,
  imagePosition = 'top'
}: InstagramPostVerticalProps) {
  const getAccentColor = () => {
    switch (type) {
      case 'roll':
        return 'bg-film-black';
      case 'lab':
        return 'bg-film-red';
      case 'tip':
        return 'bg-film-orange';
      case 'community':
        return 'bg-film-gray';
      case 'feature':
        return 'bg-film-red-light';
      case 'quote':
        return 'bg-film-black';
      case 'stats':
        return 'bg-film-orange';
      case 'gear':
        return 'bg-film-black';
      default:
        return 'bg-film-red';
    }
  };
  const getBadgeText = () => {
    switch (type) {
      case 'roll':
        return 'NEW ROLL';
      case 'lab':
        return 'LAB SPOTLIGHT';
      case 'tip':
        return 'PRO TIP';
      case 'community':
        return 'COMMUNITY';
      case 'feature':
        return 'FEATURE';
      case 'quote':
        return 'INSPIRATION';
      case 'stats':
        return 'THIS MONTH';
      case 'gear':
        return 'GEAR GUIDE';
      default:
        return 'FILM NOTES';
    }
  };
  return <div className="w-full bg-film-cream relative overflow-hidden flex flex-col shadow-lg" style={{
    aspectRatio: '3/4'
  }} data-id="element-326">
      {/* Image Layouts */}
      {image && imagePosition === 'full' && <div className="absolute inset-0" data-id="element-327">
          <img src={image} alt="" className="w-full h-full object-cover" data-id="element-328" />
          <div className="absolute inset-0 bg-gradient-to-t from-film-black/90 via-film-black/40 to-transparent" data-id="element-329"></div>
        </div>}

      {/* Top Image */}
      {image && imagePosition === 'top' && <div className="w-full h-80 relative shrink-0" data-id="element-330">
          <img src={image} alt="" className="w-full h-full object-cover" data-id="element-331" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-film-cream/20" data-id="element-332"></div>
        </div>}

      {/* Main Content Area */}
      <div className="flex-1 p-8 flex flex-col relative z-10" data-id="element-333">
        {/* Badge */}
        <div className="mb-5" data-id="element-334">
          <span className={`
            inline-block px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-white
            ${getAccentColor()}
          `} data-id="element-335">
            {getBadgeText()}
          </span>
        </div>

        {/* Title */}
        <h2 className={`font-display font-bold text-film-black mb-4 leading-tight ${imagePosition === 'full' ? 'text-white text-4xl' : 'text-3xl'}`} data-id="element-336">
          {title}
        </h2>

        {subtitle && <p className={`text-base mb-5 ${imagePosition === 'full' ? 'text-white/90' : 'text-film-gray'}`} data-id="element-337">
            {subtitle}
          </p>}

        {/* Content Container */}
        <div className="flex-1 flex flex-col justify-center" data-id="element-338">{content}</div>

        {/* Bottom Image */}
        {image && imagePosition === 'bottom' && <div className="w-full h-64 -mx-8 -mb-8 mt-8 relative" data-id="element-339">
            <img src={image} alt="" className="w-full h-full object-cover" data-id="element-340" />
          </div>}

        {/* Footer Info */}
        <div className={`mt-8 flex items-center justify-between text-sm font-medium ${imagePosition === 'full' ? 'text-white/80' : 'text-film-gray'}`} data-id="element-341">
          <span className="flex items-center gap-2" data-id="element-342">
            <Calendar className="w-4 h-4" data-id="element-343" />
            {new Date().toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric'
          })}
          </span>
          <span data-id="element-344">@filmnotesapp</span>
        </div>
      </div>
    </div>;
}